/**
 * MSSP-only application shell with partner navigation and nested route outlet.
 */
import { useState } from "react";
import { ArrowLeft, Building2, CalendarClock, ChevronUp, Layers3, LogOut, ShieldCheck } from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { getApiUrl, getCsrfToken, clearCsrfToken, clearUserId } from "../../utils/apiHelper";
import "../../styles/mssp/MsspConsole.css";

const links = [
  { to: "/mssp/customers", label: "Customers", icon: Building2 },
  { to: "/mssp/reports", label: "Summary reports", icon: CalendarClock },
  { to: "/mssp/service-plans", label: "Service catalog", icon: Layers3 },
];

export default function MsspShell() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [accountOpen, setAccountOpen] = useState(true);
  const displayName = user?.name?.trim() || user?.email || "Signed in";
  const displayRole = "mssp admin";

  const handleLogout = async () => {
    try {
      const csrfToken = await getCsrfToken();
      await fetch(getApiUrl("/api/internal/auth/logout/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearCsrfToken();
      clearUserId();
      localStorage.removeItem("user");
      navigate("/login");
    }
  };

  return (
    <div className="mssp-app">
      <aside className="mssp-sidebar">
        <div className="mssp-brand">
          <span className="mssp-brand-mark"><ShieldCheck size={20} /></span>
          <span><strong>WhiteHaX</strong><small>MSSP services</small></span>
        </div>
        <nav aria-label="MSSP service navigation">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `mssp-nav-link${isActive ? " active" : ""}`}>
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={`mssp-sidebar-footer${accountOpen ? " open" : ""}`}>
          <button
            type="button"
            className="mssp-user-block"
            onClick={() => setAccountOpen((open) => !open)}
            aria-expanded={accountOpen}
            aria-controls="mssp-account-actions"
          >
            <span className="mssp-user-block-text">
              <strong>{displayName}</strong>
              <span>{displayRole}</span>
            </span>
            <ChevronUp
              size={16}
              className={`mssp-user-chevron${accountOpen ? " open" : ""}`}
              aria-hidden="true"
            />
          </button>
          <div
            className="mssp-sidebar-actions-wrap"
            id="mssp-account-actions"
            aria-hidden={!accountOpen}
          >
            <div className="mssp-sidebar-actions">
              <button
                type="button"
                className="mssp-nav-link mssp-sidebar-action"
                onClick={() => navigate("/")}
                title="Return to WhiteHaX AI ASM"
                tabIndex={accountOpen ? 0 : -1}
              >
                <ArrowLeft size={18} />
                <span>Return to WhiteHaX AI ASM</span>
              </button>
              <button
                type="button"
                className="mssp-nav-link mssp-sidebar-action"
                onClick={handleLogout}
                title="Logout"
                tabIndex={accountOpen ? 0 : -1}
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
      <main className="mssp-main">
        <header className="mssp-topbar">
          <div>
            <strong>MSSP service console</strong>
            <span>Dedicated partner instance</span>
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
